/**
 * "Mit Google anmelden" without a Google script on our pages: the button sends the visitor to
 * Google, Google sends them back to Routes.GOOGLE_CALLBACK with a one-time code, and that page
 * hands the code to the API.
 *
 * What the sign-up page knew before the redirect (consents, ad origin) has to survive it, so it
 * waits in sessionStorage — for this tab only, and removed as soon as the callback reads it.
 */
import { Routes } from 'enums';
import type { GoogleSignInInput } from 'API/account';

const PENDING_KEY = 'pai.googleSignIn';
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

export interface PendingGoogleSignIn {
  /** Ties Google's answer to the click that started it. */
  state: string;
  signup?: GoogleSignInInput['signup'];
  /** Where a login should land afterwards (the sign-in page's redirectTo). */
  redirectTo?: string;
  /** The entry page's conversation and the entry it showed (measurement, chat outcome). */
  chatSessionId?: string;
  entry?: string;
}

/** Same URL the API accepts (auth/google-sign-in.ts GOOGLE_CALLBACK_PATH). */
export const googleRedirectUri = (): string =>
  `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}${Routes.GOOGLE_CALLBACK}`;

const randomState = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');

export const buildGoogleAuthUrl = (clientId: string, state: string): string => {
  const url = new URL(GOOGLE_AUTH_URL);
  url.search = new URLSearchParams({
    client_id: clientId,
    redirect_uri: googleRedirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    state,
    prompt: 'select_account',
  }).toString();
  return url.toString();
};

export const startGoogleSignIn = (clientId: string, pending: Omit<PendingGoogleSignIn, 'state'>): void => {
  const state = randomState();
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, state }));
  window.location.assign(buildGoogleAuthUrl(clientId, state));
};

/** Reads and removes what the click stored; null unless `state` matches. */
export const takePendingGoogleSignIn = (state: string | null): PendingGoogleSignIn | null => {
  let pending: PendingGoogleSignIn | null = null;
  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return null;
  }
  return pending && state && pending.state === state ? pending : null;
};
